// Snapshot interpolator. Entity positions in the live ClientState reflect the
// most recent snapshot. We separately track the previous snapshot so the
// renderer can lerp toward the latest target at frame rate (60 Hz) instead
// of stepping at the snapshot rate (10 Hz).
//
// The interpolator keys entities by id so it can match across snapshots and
// drop entries that disappeared (despawned shells, dead pillboxes, etc.).

import type { ClientState } from './clientState.js';

interface InterpEntity {
  prevX: number;
  prevY: number;
  prevDir: number;
  curX: number;
  curY: number;
  curDir: number;
  // Wall-clock timestamps of the prev/cur snapshot arrivals.
  prevAt: number;
  curAt: number;
}

export class Interpolator {
  private tanks = new Map<number, InterpEntity>();
  private shells = new Map<number, InterpEntity>();
  private lgms = new Map<number, InterpEntity>(); // keyed by tank id

  // Call once per tick, immediately after a snapshot has been applied to state.
  recordSnapshot(state: ClientState, now: number = performance.now()): void {
    this.absorb(this.tanks, state.tanks, now);
    this.absorb(this.shells, state.shells, now);
    // LGM interpolation reuses tank id but a separate map (LGM moves
    // independently when outside the tank).
    const lgmEntities = state.tanks.map((t) => ({ id: t.id, x: t.lgmX, y: t.lgmY, dir: 0 }));
    this.absorb(this.lgms, lgmEntities, now);
  }

  // Returns the lerp parameter t in [0..1] for an entity at the current
  // wall-clock time, plus the previous and current poses. Callers blend.
  lerpTank(id: number, now: number): { x: number; y: number; dir: number } | null {
    const e = this.tanks.get(id);
    if (!e) return null;
    return interp(e, now);
  }

  lerpShell(id: number, now: number): { x: number; y: number; dir: number } | null {
    const e = this.shells.get(id);
    if (!e) return null;
    return interp(e, now);
  }

  lerpLgm(tankId: number, now: number): { x: number; y: number } | null {
    const e = this.lgms.get(tankId);
    if (!e) return null;
    const p = interp(e, now);
    return { x: p.x, y: p.y };
  }

  private absorb(
    map: Map<number, InterpEntity>,
    list: ReadonlyArray<{ id: number; x: number; y: number; dir?: number }>,
    now: number,
  ): void {
    const seen = new Set<number>();
    for (const ent of list) {
      seen.add(ent.id);
      const existing = map.get(ent.id);
      if (existing) {
        existing.prevX = existing.curX;
        existing.prevY = existing.curY;
        existing.prevDir = existing.curDir;
        existing.prevAt = existing.curAt;
        existing.curX = ent.x;
        existing.curY = ent.y;
        existing.curDir = ent.dir ?? 0;
        existing.curAt = now;
      } else {
        map.set(ent.id, {
          prevX: ent.x,
          prevY: ent.y,
          prevDir: ent.dir ?? 0,
          curX: ent.x,
          curY: ent.y,
          curDir: ent.dir ?? 0,
          prevAt: now,
          curAt: now,
        });
      }
    }
    // Drop entities that no longer exist.
    for (const id of map.keys()) {
      if (!seen.has(id)) map.delete(id);
    }
  }
}

function interp(e: InterpEntity, now: number): { x: number; y: number; dir: number } {
  const span = e.curAt - e.prevAt;
  if (span <= 0) return { x: e.curX, y: e.curY, dir: e.curDir };
  // Render-behind by one snapshot interval so we *always* have two data
  // points bracketing the render time — t stays in [0..1], no extrapolation,
  // no rubber-band when a remote tank changes motion. Trade-off: ~one
  // snapshot interval of constant display lag (~40 ms at 25 Hz). Previous
  // approach extrapolated up to +50 % past `cur`, which jiggled visibly
  // when the target turned/stopped — bad for aiming. A later probe at
  // t≤1.2 (commit 7253489, reverted in 0aa6cca-equivalent) made boat
  // camera judder *worse* under jittered snapshot arrival, because the
  // extrapolation overshoots `cur` and then snaps back when the late
  // snapshot lands — the backward jump reads as additional motion. The
  // principled fix for the boat-camera jerk is client-side boat
  // prediction (mirror tickBoats in the predictor), not extrapolation
  // here.
  const renderTime = now - span;
  const t = Math.max(0, Math.min(1, (renderTime - e.prevAt) / span));
  return {
    x: e.prevX + (e.curX - e.prevX) * t,
    y: e.prevY + (e.curY - e.prevY) * t,
    dir: lerpDir(e.prevDir, e.curDir, t),
  };
}

function lerpDir(a: number, b: number, t: number): number {
  // Direction is 0..255 wrapping. Take the short way around.
  let d = b - a;
  if (d > 128) d -= 256;
  if (d < -128) d += 256;
  return (a + d * t + 256) % 256;
}
